import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@cleanbudget/ui/components/table";
import { cn } from "@cleanbudget/ui/lib/utils";
import { IconArrowDown, IconArrowUp, IconSelector } from "@tabler/icons-react";
import type { ReactNode } from "react";
import { firstPage, SearchLink } from "./search-params";

/**
 * The admin's list table, for every list page: columns, sortable headers and an empty state, with
 * the sort in the URL (`?sort=email&order=desc`). The page's loader fetches the rows for the URL's
 * query; the table only shows them. Pair it with `DataTableToolbar` (search, filters) above and
 * `DataTablePagination` below.
 *
 * ```tsx
 * <DataTable columns={columns} rows={data.items} rowKey={(e) => e.id} sort={search.sort} order={search.order}
 *   empty={<DataTableEmpty filtered={...} />} />
 * ```
 */

export interface Column<Row> {
	/** Unique per table; for a sortable column, also the `sort` value the API takes. */
	readonly id: string;
	readonly header: ReactNode;
	readonly cell: (row: Row) => ReactNode;
	readonly sortable?: boolean;
	/** Left out on a phone; show what matters in the other columns there. */
	readonly secondary?: boolean;
	readonly align?: "left" | "right";
}

export function DataTable<Row>({
	columns,
	rows,
	rowKey,
	sort,
	order,
	empty,
	label,
}: {
	columns: readonly Column<Row>[];
	rows: readonly Row[];
	rowKey: (row: Row) => string;
	sort?: string;
	order?: "asc" | "desc";
	/** Shown instead of rows when there are none. */
	empty: ReactNode;
	/** The table's accessible name (the page title). */
	label: string;
}) {
	const cellClass = (column: Column<Row>) =>
		cn(column.secondary && "hidden md:table-cell", column.align === "right" && "text-right");

	return (
		<div className="overflow-hidden rounded-2xl bg-card ring-1 ring-foreground/10">
			<Table aria-label={label}>
				<TableHeader>
					<TableRow className="hover:bg-transparent">
						{columns.map((column) => (
							<TableHead
								key={column.id}
								className={cellClass(column)}
								aria-sort={
									column.sortable && sort === column.id
										? order === "desc"
											? "descending"
											: "ascending"
										: undefined
								}
							>
								{column.sortable ? (
									<SortHeader column={column} active={sort === column.id} order={order} />
								) : (
									column.header
								)}
							</TableHead>
						))}
					</TableRow>
				</TableHeader>
				<TableBody>
					{rows.length === 0 ? (
						<TableRow className="hover:bg-transparent">
							<TableCell colSpan={columns.length} className="py-10 text-center whitespace-normal">
								{empty}
							</TableCell>
						</TableRow>
					) : (
						rows.map((row) => (
							<TableRow key={rowKey(row)}>
								{columns.map((column) => (
									<TableCell key={column.id} className={cellClass(column)}>
										{column.cell(row)}
									</TableCell>
								))}
							</TableRow>
						))
					)}
				</TableBody>
			</Table>
		</div>
	);
}

/** A header that sorts by its column; on the active column it flips the order. */
function SortHeader<Row>({
	column,
	active,
	order,
}: {
	column: Column<Row>;
	active: boolean;
	order: "asc" | "desc" | undefined;
}) {
	const next = active && order !== "desc" ? "desc" : "asc";
	const Icon = !active ? IconSelector : order === "desc" ? IconArrowDown : IconArrowUp;
	return (
		<SearchLink
			search={(previous) => firstPage({ ...previous, sort: column.id, order: next })}
			className={cn(
				"-mx-2 inline-flex items-center gap-1 rounded-lg px-2 py-1 transition-colors hover:bg-muted",
				column.align === "right" && "flex-row-reverse",
			)}
		>
			{column.header}
			<Icon className={cn("size-3.5", !active && "text-muted-foreground")} aria-hidden />
		</SearchLink>
	);
}
